import {
  useCallback,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import clsx from 'clsx';
import { berthValidationSchema } from '@/utils/validations';
import { Modal } from '@/components';
import PriceInput from '@/components/inputControls/PriceInput';
import CircularProgress from '@mui/material/CircularProgress';
import {
  IBerthData,
  BerthStatus,
  Mooring,
} from '@/types/berth-management.types';
import { IBerthCategoryData } from '@/types/berth-category.types';
import { IHarborData } from '@/types/harbor-management.types';
import { useGetCategoriesQuery } from '@/services/BerthCategoryService';
import { useGetHarborsQuery } from '@/services/HarborManagementService';
import { useGetMooringsQuery } from '@/services/BerthManagementService';
import { RootState } from '@/store/store';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useGetPiersQuery } from '@/services/PierManagementService';
import { PierListQueryParams } from '@/types/pier-management.types';
import usePagination from '@/hooks/usePagination';
import { ChevronDown } from 'lucide-react';
import { BerthType, Seasons } from '@/constant';
import NumberInput from '@/components/inputControls/NumberInput';

interface IBerthDataExtended extends IBerthData {
  pier?: { id: string; name: string } | null;
}

interface UnavailabilityData {
  start_date?: string;
  end_date?: string;
}

interface BerthModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit' | 'view';
  berthId?: string;
  formData: Partial<IBerthDataExtended>;
  setFormData: Dispatch<SetStateAction<Partial<IBerthDataExtended>>>;
  errors: { [key: string]: string };
  setErrors: Dispatch<SetStateAction<{ [key: string]: string }>>;
  onSubmit: (unavailabilityData?: UnavailabilityData) => Promise<void>;
  isSubmitting?: boolean;
  className?: string;
}

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const BerthModal = ({
  mode,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit: onSubmitProp,
  isSubmitting,
  open,
  onClose,
  className,
  ...props
}: BerthModalProps) => {
  const [localFormData, setLocalFormData] =
    useState<Partial<IBerthDataExtended>>(formData);
  const [unavailabilityData, setUnavailabilityData] =
    useState<UnavailabilityData>({});
  const [seasonalPricing, setSeasonalPricing] = useState<
    Partial<Record<Seasons, number>>
  >({
    [Seasons.SPRING]: 0,
    [Seasons.SUMMER]: 0,
    [Seasons.AUTUMN]: 0,
    [Seasons.WINTER]: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { data: categories, isLoading: isCategoriesLoading } =
    useGetCategoriesQuery({
      harbor_id: formData.harbor_id || undefined,
    });
  const { data: harbors, isLoading: isHarborsLoading } = useGetHarborsQuery({
    search: debouncedSearchTerm,
  });
  const { data: moorings, isLoading: isMooringsLoading } =
    useGetMooringsQuery();

  const selectedHarborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );
  const location = useLocation();
  const isHarborDetailsRoute = location.pathname === '/harbors/details';
  const [isHarborDropdownOpen, setIsHarborDropdownOpen] = useState(false);
  const [isPierDropdownOpen, setIsPierDropdownOpen] = useState(false);
  const [isBerthTypeDropdownOpen, setIsBerthTypeDropdownOpen] = useState(false);
  const [pierSearchTerm, setPierSearchTerm] = useState('');
  const [prefix, setPrefix] = useState(formData.pier_prefix || '');
  const isPrefixCustomRef = useRef(false);
  const [generatedBerthRange, setGeneratedBerthRange] = useState<string>('');
  const harborDropdownRef = useRef<HTMLDivElement>(null);
  const pierDropdownRef = useRef<HTMLDivElement>(null);
  const berthTypeDropdownRef = useRef<HTMLDivElement>(null);
  const pierButtonRef = useRef<HTMLDivElement>(null);
  const pierSearchInputRef = useRef<HTMLInputElement>(null);
  const [pierHighlightIndex, setPierHighlightIndex] = useState<number>(-1);
  const berthTypeButtonRef = useRef<HTMLDivElement>(null);
  const [berthTypeHighlightIndex, setBerthTypeHighlightIndex] =
    useState<number>(-1);
  const [selectedPierPooledCapacity, setSelectedPierPooledCapacity] = useState<
    number | null
  >(null);
  const [selectedPierCapacityMode, setSelectedPierCapacityMode] = useState<
    string | null
  >(null);
  const isSelectedPierSingleBerthPool =
    selectedPierCapacityMode === 'SINGLE_BERTH_POOL';
  const { debouncedValue } = usePagination();
  const queryParams: PierListQueryParams = {
    search: debouncedValue || undefined,
    ...(formData.harbor_id && { harbor_id: formData.harbor_id }),
    page: 1,
    limit: 99,
  };

  const { data: piersData } = useGetPiersQuery(queryParams);

  const shouldShowSeasonalPricing =
    formData.berth_type === BerthType.SEASONAL && !!formData.harbor_id;

  const seasonalPricingFromFormData = (
    formData as unknown as { seasonal_pricing?: unknown }
  )?.seasonal_pricing;

  const selectedHarborForSeasonal = shouldShowSeasonalPricing
    ? (harbors?.data as IHarborData[] | undefined)?.find(
      (h) => h.id === formData.harbor_id
    )
    : undefined;

  const isSeasonCompletelyClosed = useCallback(
    (ranges?: { is_closed?: boolean }[] | null) => {
      if (!ranges || ranges.length === 0) return false;
      return ranges.every((r) => !!r.is_closed);
    },
    []
  );

  const seasonalClosedMap = useMemo(() => {
    if (!selectedHarborForSeasonal?.seasonal_dates) {
      return {
        [Seasons.SPRING]: false,
        [Seasons.SUMMER]: false,
        [Seasons.AUTUMN]: false,
        [Seasons.WINTER]: false,
      } as Record<Seasons, boolean>;
    }

    return {
      [Seasons.SPRING]: isSeasonCompletelyClosed(
        selectedHarborForSeasonal.seasonal_dates.spring as
        | { is_closed?: boolean }[]
        | null
        | undefined
      ),
      [Seasons.SUMMER]: isSeasonCompletelyClosed(
        selectedHarborForSeasonal.seasonal_dates.summer as
        | { is_closed?: boolean }[]
        | null
        | undefined
      ),
      [Seasons.AUTUMN]: isSeasonCompletelyClosed(
        selectedHarborForSeasonal.seasonal_dates.autumn as
        | { is_closed?: boolean }[]
        | null
        | undefined
      ),
      [Seasons.WINTER]: isSeasonCompletelyClosed(
        selectedHarborForSeasonal.seasonal_dates.winter as
        | { is_closed?: boolean }[]
        | null
        | undefined
      ),
    } as Record<Seasons, boolean>;
  }, [isSeasonCompletelyClosed, selectedHarborForSeasonal?.seasonal_dates]);

  const lastSyncedSeasonalPricingRef = useRef<string>('');
  const lastHydratedSeasonalPricingRef = useRef<string>('');
  const hasHydratedSeasonalPricingRef = useRef<boolean>(false);

  const normalizeSeasonalPricing = useCallback((input: unknown) => {
    const record =
      input && typeof input === 'object'
        ? (input as Record<string, unknown>)
        : {};

    const normalized: Partial<Record<Seasons, number>> = {};
    (
      [Seasons.SPRING, Seasons.SUMMER, Seasons.AUTUMN, Seasons.WINTER] as const
    ).forEach((season) => {
      const value = Number(record[season]) || 0;
      if (Number.isFinite(value) && value !== 0) normalized[season] = value;
    });

    return Object.keys(normalized).length > 0 ? normalized : undefined;
  }, []);

  const serializeSeasonalPricing = useCallback(
    (input: unknown) => JSON.stringify(normalizeSeasonalPricing(input) ?? {}),
    [normalizeSeasonalPricing]
  );

  useEffect(() => {
    if (!open) return;

    if (shouldShowSeasonalPricing) {
      // In edit/view mode, avoid syncing "empty" values into formData before
      // we hydrate from the API response (prevents flicker / rapid changes).
      if (!hasHydratedSeasonalPricingRef.current) {
        const existingSerialized = serializeSeasonalPricing(
          seasonalPricingFromFormData
        );
        if (existingSerialized !== JSON.stringify({})) {
          return;
        }
      }

      const nextSeasonalPricing: Partial<Record<Seasons, number>> = {
        [Seasons.SPRING]: Number(seasonalPricing[Seasons.SPRING]) || 0,
        [Seasons.SUMMER]: Number(seasonalPricing[Seasons.SUMMER]) || 0,
        [Seasons.AUTUMN]: Number(seasonalPricing[Seasons.AUTUMN]) || 0,
        [Seasons.WINTER]: Number(seasonalPricing[Seasons.WINTER]) || 0,
      };

      const payload = normalizeSeasonalPricing(nextSeasonalPricing);
      const serialized = JSON.stringify(payload ?? {});
      if (serialized === lastSyncedSeasonalPricingRef.current) return;
      lastSyncedSeasonalPricingRef.current = serialized;
      lastHydratedSeasonalPricingRef.current = serialized;
      hasHydratedSeasonalPricingRef.current = true;

      setFormData((prev) => ({
        ...prev,
        seasonal_pricing: payload,
      }));
    } else {
      if (lastSyncedSeasonalPricingRef.current !== '') {
        lastSyncedSeasonalPricingRef.current = '';
      }
      hasHydratedSeasonalPricingRef.current = false;
      setFormData((prev) => {
        if (!('seasonal_pricing' in prev)) return prev;
        const next = { ...prev } as Record<string, unknown>;
        delete next.seasonal_pricing;
        return next as Partial<IBerthDataExtended>;
      });
    }
  }, [
    open,
    shouldShowSeasonalPricing,
    seasonalPricing,
    seasonalPricingFromFormData,
    normalizeSeasonalPricing,
    serializeSeasonalPricing,
    setFormData,
  ]);

  useEffect(() => {
    if (!open) return;

    if (formData.berth_type !== BerthType.SEASONAL) {
      setSeasonalPricing({
        [Seasons.SPRING]: 0,
        [Seasons.SUMMER]: 0,
        [Seasons.AUTUMN]: 0,
        [Seasons.WINTER]: 0,
      });
      lastHydratedSeasonalPricingRef.current = '';
      hasHydratedSeasonalPricingRef.current = false;
      return;
    }

    const existing = seasonalPricingFromFormData;
    const existingSerialized = serializeSeasonalPricing(existing);
    if (existingSerialized === lastHydratedSeasonalPricingRef.current) return;
    lastHydratedSeasonalPricingRef.current = existingSerialized;
    hasHydratedSeasonalPricingRef.current = true;

    const normalized = normalizeSeasonalPricing(existing);
    const normalizedRecord = (normalized ?? {}) as Partial<
      Record<Seasons, number>
    >;
    setSeasonalPricing({
      [Seasons.SPRING]: Number(normalizedRecord[Seasons.SPRING]) || 0,
      [Seasons.SUMMER]: Number(normalizedRecord[Seasons.SUMMER]) || 0,
      [Seasons.AUTUMN]: Number(normalizedRecord[Seasons.AUTUMN]) || 0,
      [Seasons.WINTER]: Number(normalizedRecord[Seasons.WINTER]) || 0,
    });
  }, [
    open,
    formData.berth_type,
    formData.harbor_id,
    seasonalPricingFromFormData,
    normalizeSeasonalPricing,
    serializeSeasonalPricing,
  ]);

  useEffect(() => {
    if (!formData) return;

    let updatedMooringStyle = formData.mooring_style || '';

    if (formData.mooring_style && moorings?.length && mode !== 'create') {
      const mooring = moorings.find(
        (m: Mooring) => m.name === formData.mooring_style
      );
      updatedMooringStyle = mooring ? mooring.name : formData.mooring_style;
    }

    let updatedCategoryId = formData.category_id || '';

    if (formData.category_id && categories?.data && mode !== 'create') {
      const categoryExists = categories.data.some(
        (category: IBerthCategoryData) => category.id === formData.category_id
      );
      if (!categoryExists) {
        updatedCategoryId = '';
      }
    }

    setLocalFormData(() => {
      return {
        ...formData,
        mooring_style: updatedMooringStyle,
        category_id: updatedCategoryId,
      };
    });

    setPrefix(formData.pier_prefix || '');
  }, [formData, moorings, mode, categories]);

  useEffect(() => {
    if (mode === 'create' && localFormData.quantity && prefix) {
      const count = Number(localFormData.quantity) || 0;
      if (count > 0) {
        const berthRange = `${prefix}-1 to ${prefix}-${count}`;
        setGeneratedBerthRange(berthRange);
      } else {
        setGeneratedBerthRange('');
      }
    } else {
      setGeneratedBerthRange('');
    }
  }, [localFormData.quantity, prefix, mode]);

  useEffect(() => {
    if (
      mode !== 'create' ||
      !open ||
      !selectedHarborId ||
      !isHarborDetailsRoute ||
      !Array.isArray(harbors?.data)
    )
      return;

    const selectedHarbor = harbors.data.find((h) => h.id === selectedHarborId);
    if (selectedHarbor) {
      setFormData((prev) => ({
        ...prev,
        harbor_id: selectedHarborId,
        harbor: {
          id: selectedHarbor.id,
          name: selectedHarbor.name,
        },
        pier_id: undefined,
        pier_prefix: '',
        pier: null,
      }));
    }
  }, [open, mode, selectedHarborId, isHarborDetailsRoute, setFormData, harbors?.data]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        harborDropdownRef.current &&
        !harborDropdownRef.current.contains(event.target as Node)
      ) {
        setIsHarborDropdownOpen(false);
      }
      if (
        pierDropdownRef.current &&
        !pierDropdownRef.current.contains(event.target as Node)
      ) {
        setIsPierDropdownOpen(false);
      }
      if (
        berthTypeDropdownRef.current &&
        !berthTypeDropdownRef.current.contains(event.target as Node)
      ) {
        setIsBerthTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus Pier field when modal opens (if pier field is visible)
  useEffect(() => {
    if (open && formData.harbor_id) {
      setTimeout(() => {
        pierButtonRef.current?.focus();
      }, 0);
    }
  }, [open, formData.harbor_id]);

  // When dropdown opens, move focus to the search input
  useEffect(() => {
    if (isPierDropdownOpen) {
      setTimeout(() => pierSearchInputRef.current?.focus(), 0);
    }
  }, [isPierDropdownOpen]);

  const getFilteredPiers = useCallback(() => {
    const list = (
      (piersData?.data as
        | {
          id: string;
          name: string;
          pooled_capacity?: number | null;
        }[]
        | undefined) || []
    ).slice();
    if (!pierSearchTerm) return list;
    const term = pierSearchTerm.toLowerCase();
    return list.filter((pier) => pier.name?.toLowerCase().includes(term));
  }, [piersData?.data, pierSearchTerm]);

  // Keep highlighted index in range when filter changes
  useEffect(() => {
    if (isPierDropdownOpen) {
      const len = getFilteredPiers().length;
      setPierHighlightIndex((prev) => {
        if (len === 0) return -1;
        return Math.min(prev < 0 ? 0 : prev, len - 1);
      });
    }
  }, [pierSearchTerm, piersData?.data, isPierDropdownOpen, getFilteredPiers]);

  const handlePierKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (mode === 'view' || isSubmitting || !formData.harbor_id) return;

    const filtered = getFilteredPiers();
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isPierDropdownOpen) {
        const index = pierHighlightIndex >= 0 ? pierHighlightIndex : 0;
        const option = filtered[index];
        if (option) {
          handlePierSelect(option);
        }
      } else {
        setIsPierDropdownOpen(true);
        const selectedIdx = filtered.findIndex(
          (p) => p.id === formData.pier_id
        );
        setPierHighlightIndex(selectedIdx >= 0 ? selectedIdx : 0);
      }
    } else if (e.key === ' ' && !isPierDropdownOpen) {
      e.preventDefault();
      setIsPierDropdownOpen(true);
      const selectedIdx = filtered.findIndex((p) => p.id === formData.pier_id);
      setPierHighlightIndex(selectedIdx >= 0 ? selectedIdx : 0);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isPierDropdownOpen) {
        setIsPierDropdownOpen(true);
        const selectedIdx = filtered.findIndex(
          (p) => p.id === formData.pier_id
        );
        setPierHighlightIndex(selectedIdx >= 0 ? selectedIdx : 0);
      } else {
        setPierHighlightIndex((prev) => {
          const next =
            prev < 0 ? 0 : Math.min(prev + 1, Math.max(filtered.length - 1, 0));
          return next;
        });
      }
    } else if (e.key === 'ArrowUp' && isPierDropdownOpen) {
      e.preventDefault();
      setPierHighlightIndex((prev) => {
        const next = prev < 0 ? 0 : Math.max(prev - 1, 0);
        return next;
      });
    } else if (e.key === 'Escape' && isPierDropdownOpen) {
      e.preventDefault();
      e.stopPropagation();
      setIsPierDropdownOpen(false);
      // Return focus to the combobox so focus stays within the modal
      setTimeout(() => pierButtonRef.current?.focus(), 0);
    }
  };

  const handleHarborDropdownToggle = () => {
    if (mode === 'view' || isSubmitting || isHarborDetailsRoute) return;
    setIsHarborDropdownOpen(!isHarborDropdownOpen);
  };

  const handleHarborSelect = (harbor: IHarborData) => {
    setFormData((prev) => ({
      ...prev,
      harbor_id: harbor.id,
      harbor: { id: harbor.id, name: harbor.name },
      pier_id: undefined,
      pier_prefix: '',
      pier: null,
    }));
    setLocalFormData((prev) => ({
      ...prev,
      harbor_id: harbor.id,
      harbor: { id: harbor.id, name: harbor.name },
      pier_id: undefined,
      pier_prefix: '',
      pier: null,
    }));
    setPrefix('');
    isPrefixCustomRef.current = false;
    setSelectedPierPooledCapacity(null);
    setSelectedPierCapacityMode(null);
    setIsHarborDropdownOpen(false);
    setSearchTerm('');
    validateField('harbor_id', harbor.id);
  };

  const handlePierDropdownToggle = () => {
    if (mode === 'view' || isSubmitting || !formData.harbor_id) return;
    setIsPierDropdownOpen(!isPierDropdownOpen);
  };

  const handlePierSelect = (pier: {
    id: string;
    name: string;
    pooled_capacity?: number | null;
    capacity_mode?: string | null;
  }) => {
    // In create mode, keep following the selected pier's name until the user
    // types a custom prefix - once customized, pier changes no longer override it.
    const isStillDefaultPrefix = mode === 'create' && !isPrefixCustomRef.current;
    const prefixValue = (
      isStillDefaultPrefix
        ? pier.name.replace(/\s/g, '')
        : prefix || pier.name.replace(/\s/g, '')
    ).normalize('NFC');
    setFormData((prev) => ({
      ...prev,
      pier_id: pier.id,
      pier_prefix: prefixValue,
      pier: { id: pier.id, name: pier.name },
    }));
    setLocalFormData((prev) => ({
      ...prev,
      pier_id: pier.id,
      pier_prefix: prefixValue,
      pier: { id: pier.id, name: pier.name },
      // A SINGLE_BERTH_POOL pier can only ever have one berth row.
      ...(pier.capacity_mode === 'SINGLE_BERTH_POOL' && { quantity: 1 }),
    }));
    setPrefix(prefixValue);
    setSelectedPierPooledCapacity(pier.pooled_capacity ?? null);
    setSelectedPierCapacityMode(pier.capacity_mode ?? null);
    setIsPierDropdownOpen(false);
    validateField('pier_id', pier.id);
    validateField('pier_prefix', prefixValue);
  };

  const validateField = useCallback(
    async (name: keyof IBerthDataExtended | string, value: unknown) => {
      if (
        name === 'price' &&
        (mode === 'create' || mode === 'edit') &&
        Number(localFormData.berth_type) === BerthType.YEARLY
      ) {
        const numeric = Number(value);
        const hasValue = value !== '' && value !== undefined && value !== null;

        if (!hasValue || !Number.isFinite(numeric)) {
          setErrors((prev) => ({
            ...prev,
            price: 'Price is required',
          }));
          return;
        }

        if (numeric <= 0) {
          setErrors((prev) => ({
            ...prev,
            price: 'Price must be positive',
          }));
          return;
        }
      }

      try {
        await berthValidationSchema.validateAt(
          name,
          { ...localFormData, [name]: value, mode },
          { abortEarly: true }
        );
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      } catch (err) {
        const error = err as Error;
        setErrors((prev) => ({
          ...prev,
          [name]: error.message,
        }));
      }
    },
    [localFormData, setErrors, mode]
  );

  const berthTypeOptions: { value: BerthType; label: string }[] = [
    { value: BerthType.TRANSIENT, label: 'Transient' },
    { value: BerthType.SEASONAL, label: 'Seasonal' },
    { value: BerthType.YEARLY, label: 'Yearly' },
  ];

  const getBerthTypeLabel = (value?: number) => {
    const option = berthTypeOptions.find((o) => o.value === value);
    return option?.label;
  };

  const handleBerthTypeSelect = useCallback(
    (option: { value: BerthType; label: string }) => {
      setFormData((prev) => ({
        ...prev,
        berth_type: option.value,
      }));
      setLocalFormData((prev) => ({
        ...prev,
        berth_type: option.value,
      }));
      setIsBerthTypeDropdownOpen(false);
      validateField('berth_type', option.value);
      setTimeout(() => berthTypeButtonRef.current?.focus(), 0);
    },
    [setFormData, validateField]
  );

  const handleBerthTypeKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (mode === 'view' || isSubmitting) return;

    const selectedIdx = berthTypeOptions.findIndex(
      (o) => o.value === formData.berth_type
    );

    if (e.key === 'Enter') {
      e.preventDefault();
      if (!isBerthTypeDropdownOpen) {
        setIsBerthTypeDropdownOpen(true);
        setBerthTypeHighlightIndex(selectedIdx >= 0 ? selectedIdx : 0);
        return;
      }

      const idx = berthTypeHighlightIndex >= 0 ? berthTypeHighlightIndex : 0;
      const option = berthTypeOptions[idx];
      if (option) handleBerthTypeSelect(option);
    } else if (e.key === ' ' && !isBerthTypeDropdownOpen) {
      e.preventDefault();
      setIsBerthTypeDropdownOpen(true);
      setBerthTypeHighlightIndex(selectedIdx >= 0 ? selectedIdx : 0);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isBerthTypeDropdownOpen) {
        setIsBerthTypeDropdownOpen(true);
        setBerthTypeHighlightIndex(selectedIdx >= 0 ? selectedIdx : 0);
        return;
      }
      setBerthTypeHighlightIndex((prev) =>
        Math.min(prev < 0 ? 0 : prev + 1, berthTypeOptions.length - 1)
      );
    } else if (e.key === 'ArrowUp') {
      if (!isBerthTypeDropdownOpen) return;
      e.preventDefault();
      setBerthTypeHighlightIndex((prev) =>
        Math.max(prev < 0 ? 0 : prev - 1, 0)
      );
    } else if (e.key === 'Escape') {
      if (!isBerthTypeDropdownOpen) return;
      e.preventDefault();
      e.stopPropagation();
      setIsBerthTypeDropdownOpen(false);
      setTimeout(() => berthTypeButtonRef.current?.focus(), 0);
    }
  };

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setLocalFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Custom validation for quantity against pooled_capacity
      if (name === 'quantity' && selectedPierPooledCapacity !== null) {
        const quantityValue = Number(value);
        if (quantityValue > selectedPierPooledCapacity) {
          setErrors((prev) => ({
            ...prev,
            quantity: `Quantity cannot exceed the pier's pooled capacity of ${selectedPierPooledCapacity}`,
          }));
          return;
        }
      }

      validateField(name, value);
    },
    [validateField, setFormData, selectedPierPooledCapacity, setErrors]
  );

  const handlePrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPrefix = e.target.value.normalize('NFC');
    isPrefixCustomRef.current = true;
    setPrefix(newPrefix);
    setLocalFormData((prev) => ({
      ...prev,
      pier_prefix: newPrefix,
    }));
    setFormData((prev) => ({
      ...prev,
      pier_prefix: newPrefix,
    }));
    validateField('pier_prefix', newPrefix);
  };

  const handleAmenityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, checked } = e.target;
      setLocalFormData((prev) => {
        const currentAmenities = prev.amenities ?? [];
        const updatedAmenities = checked
          ? [...currentAmenities, value]
          : currentAmenities.filter((amenity) => amenity !== value);
        return { ...prev, amenities: updatedAmenities };
      });
      const currentAmenities = formData.amenities ?? [];
      const updatedAmenities = checked
        ? [...currentAmenities, value]
        : currentAmenities.filter((amenity) => amenity !== value);
      setFormData({
        ...formData,
        amenities: updatedAmenities,
      });
      validateField(
        'amenities',
        checked
          ? [...(localFormData.amenities ?? []), value]
          : (localFormData.amenities ?? []).filter(
            (amenity) => amenity !== value
          )
      );
    },
    [validateField, localFormData.amenities, setFormData, formData]
  );

  const handleUnavailabilityChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setUnavailabilityData((prev) => ({
      ...prev,
      [name]: value,
    }));
    validateField(name, value);
    setLocalFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsBerthTypeDropdownOpen(false);

      if (
        !shouldShowSeasonalPricing &&
        (mode === 'create' || mode === 'edit') &&
        (Number(localFormData.berth_type) === BerthType.TRANSIENT ||
          Number(localFormData.berth_type) === BerthType.YEARLY)
      ) {
        const value = localFormData.price;
        const hasValue = value !== undefined && value !== null;
        const numeric = Number(value);

        if (!hasValue || !Number.isFinite(numeric)) {
          setErrors((prev) => ({
            ...prev,
            price: 'Price is required',
          }));
          return;
        }

        if (numeric <= 0) {
          setErrors((prev) => ({
            ...prev,
            price: 'Price must be positive',
          }));
          return;
        }
      }

      const formDataWithCount: Record<string, unknown> = {
        ...localFormData,
        count: localFormData.quantity,
        pier_id: localFormData.pier_id,
        pier_prefix: prefix,
        mooring_style: localFormData.mooring_style,
      };

      if (
        localFormData.berth_type === BerthType.SEASONAL &&
        localFormData.harbor_id
      ) {
        const nextSeasonalPricing: Partial<Record<Seasons, number>> = {
          [Seasons.SPRING]: Number(seasonalPricing[Seasons.SPRING]) || 0,
          [Seasons.SUMMER]: Number(seasonalPricing[Seasons.SUMMER]) || 0,
          [Seasons.AUTUMN]: Number(seasonalPricing[Seasons.AUTUMN]) || 0,
          [Seasons.WINTER]: Number(seasonalPricing[Seasons.WINTER]) || 0,
        };

        // Send only seasons that have a non-zero value (matches expected API payload style)
        const filtered: Partial<Record<Seasons, number>> = {};
        (Object.entries(nextSeasonalPricing) as [Seasons, number][]).forEach(
          ([season, value]) => {
            if (Number.isFinite(value) && value !== 0) filtered[season] = value;
          }
        );

        if (Object.keys(filtered).length > 0) {
          formDataWithCount.seasonal_pricing = filtered;
        } else {
          delete formDataWithCount.seasonal_pricing;
        }
      } else {
        delete formDataWithCount.seasonal_pricing;
      }

      setFormData(formDataWithCount);
      await onSubmitProp(
        Number(localFormData.status) === BerthStatus.NOT_AVAILABLE
          ? unavailabilityData
          : undefined
      );
    },
    [
      localFormData,
      mode,
      onSubmitProp,
      setFormData,
      setErrors,
      prefix,
      unavailabilityData,
      seasonalPricing,
      shouldShowSeasonalPricing,
    ]
  );

  useEffect(() => {
    if (!open) setIsBerthTypeDropdownOpen(false);
  }, [open]);

  if ((mode === 'edit' || mode === 'view') && open && !formData.id) {
    return (
      <Modal open={open} onClose={onClose} aria-label="Berth modal">
        <div className="flex justify-center p-8">
          <CircularProgress aria-label="Loading berth data" />
        </div>
      </Modal>
    );
  }

  const amenityOptions = [
    { value: 'WATER', label: 'Water' },
    { value: 'ELECTRICITY_16A', label: 'Electricity (16A)' },
    { value: 'ELECTRICITY_32A', label: 'Electricity (32A)' },
    { value: 'WIFI', label: 'WiFi' },
    { value: 'PARKING', label: 'Parking' },
  ];

  // Compute filtered list once per render for a11y props and rendering
  const filteredPiers = getFilteredPiers();

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx('flex items-center justify-center', className)}
      aria-labelledby="berth-modal-title"
      aria-describedby="berth-modal-description"
      {...props}
      data-testid="berth-modal"
    >
      <div className="bg-gray-100 p-8 w-full max-w-3xl mx-auto rounded-2xl shadow-2xl flex flex-col transform transition-all duration-200">
        <h1
          id="berth-modal-title"
          className="text-2xl font-bold text-gray-900 mb-6"
        >
          {mode === 'create'
            ? 'Create New Berth'
            : mode === 'edit'
              ? 'Edit Berth'
              : 'View Berth'}
        </h1>
        <div id="berth-modal-description" className="sr-only">
          {mode === 'create'
            ? 'Form to create a new berth'
            : mode === 'edit'
              ? 'Form to edit berth details'
              : 'View berth details'}
        </div>
        <div className="flex-1 overflow-y-auto max-h-[70vh] pr-4 custom-scrollbar">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
            {/* Harbor */}
            <div className="col-span-1">
              <label
                id="harbor_id-label"
                htmlFor="harbor_id"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Harbor
              </label>
              <div className="relative" ref={harborDropdownRef}>
                <div
                  id="harbor_id"
                  className={clsx(
                    'input input-bordered w-full h-12 flex items-center justify-between rounded-lg bg-gray-200 border-2 transition-all duration-200',
                    {
                      'cursor-pointer hover:border-blue-500':
                        !isSubmitting &&
                        mode !== 'view' &&
                        !isHarborDetailsRoute,
                      'cursor-not-allowed bg-gray-100 opacity-50':
                        isSubmitting || mode === 'view' || isHarborDetailsRoute,
                      'border-blue-500 shadow-md':
                        isHarborDropdownOpen &&
                        mode !== 'view' &&
                        !isHarborDetailsRoute,
                      'border-red-500': errors.harbor_id,
                    }
                  )}
                  onClick={handleHarborDropdownToggle}
                  aria-labelledby="harbor_id-label"
                  aria-invalid={!!errors.harbor_id}
                  aria-describedby={
                    errors.harbor_id ? 'harbor_id-error' : undefined
                  }
                  aria-haspopup="listbox"
                  aria-expanded={isHarborDropdownOpen}
                  aria-controls="harbor-listbox"
                  role="combobox"
                >
                  <div className="truncate text-gray-900">
                    {formData.harbor?.name ||
                      (isHarborsLoading
                        ? 'Loading harbors...'
                        : 'Select harbor')}
                  </div>
                  <div className="flex items-center">
                    <svg
                      className={clsx(
                        'w-5 h-5 text-gray-400 transition-transform duration-200',
                        isHarborDropdownOpen && 'transform rotate-180'
                      )}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
                {isHarborDropdownOpen &&
                  !isHarborsLoading &&
                  mode !== 'view' &&
                  !isHarborDetailsRoute && (
                    <div
                      id="harbor-listbox"
                      role="listbox"
                      className="absolute z-10 w-full mt-2 bg-gray-100 border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                    >
                      <div className="p-3 border-b">
                        <input
                          type="text"
                          placeholder="Search harbors..."
                          className="w-full p-2 border rounded-lg focus:ring-blue-500 focus:outline-none"
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setIsHarborDropdownOpen(true);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {harbors?.data && harbors?.data?.length > 0 ? (
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        harbors?.data?.map((harbor: any) => (
                          <div
                            key={harbor.id}
                            role="option"
                            aria-selected={formData.harbor_id === harbor.id}
                            className={clsx(
                              'px-4 py-2 cursor-pointer hover:bg-blue-50 transition-colors duration-150',
                              formData.harbor_id === harbor.id && 'bg-blue-100'
                            )}
                            onClick={() => handleHarborSelect(harbor)}
                          >
                            {harbor.name}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          {searchTerm
                            ? 'No harbors found'
                            : 'No harbors available'}
                        </div>
                      )}
                    </div>
                  )}
                {errors.harbor_id && (
                  <p
                    id="harbor_id-error"
                    className="text-red-500 text-xs mt-2"
                    role="alert"
                  >
                    {errors.harbor_id}
                  </p>
                )}
              </div>
            </div>

            {/*Berth Type*/}
            <div className="col-span-1">
              <label
                id="berth_type-label"
                htmlFor="berth_type-input"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Berth Type
              </label>
              <div className="relative" ref={berthTypeDropdownRef}>
                <div
                  id="berth_type-input"
                  className={clsx(
                    'input input-bordered w-full h-12 flex items-center justify-between rounded-lg bg-gray-200 border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500',
                    {
                      'cursor-pointer hover:border-blue-500':
                        !isSubmitting && mode !== 'view',
                      'cursor-not-allowed bg-gray-100 opacity-50':
                        isSubmitting || mode === 'view',
                      'border-blue-500 shadow-md':
                        isBerthTypeDropdownOpen && mode !== 'view',
                      'border-red-500': errors.berth_type,
                    }
                  )}
                  ref={berthTypeButtonRef}
                  onClick={() => {
                    if (mode === 'view' || isSubmitting) return;
                    setIsBerthTypeDropdownOpen(!isBerthTypeDropdownOpen);
                    const selectedIdx = berthTypeOptions.findIndex(
                      (o) => o.value === formData.berth_type
                    );
                    setBerthTypeHighlightIndex(
                      selectedIdx >= 0 ? selectedIdx : 0
                    );
                  }}
                  onKeyDown={handleBerthTypeKeyDown}
                  role="combobox"
                  tabIndex={0}
                  aria-expanded={isBerthTypeDropdownOpen}
                  aria-haspopup="listbox"
                  aria-labelledby="berth_type-label"
                  aria-controls="berth-type-listbox"
                  aria-activedescendant={
                    isBerthTypeDropdownOpen &&
                      berthTypeHighlightIndex >= 0 &&
                      berthTypeOptions[berthTypeHighlightIndex]
                      ? `berth-type-option-${berthTypeOptions[berthTypeHighlightIndex].value}`
                      : undefined
                  }
                  aria-invalid={!!errors.berth_type}
                  aria-describedby={
                    errors.berth_type ? 'berth_type-error' : undefined
                  }
                >
                  <span
                    className={clsx(
                      'truncate text-gray-900 dark:text-gray-900 contrast:text-white',
                      !formData.berth_type &&
                      'text-gray-500 dark:text-gray-700 contrast:text-gray-200'
                    )}
                  >
                    {getBerthTypeLabel(formData.berth_type) ||
                      'Select Berth Type'}
                  </span>
                  <ChevronDown
                    className={clsx(
                      'w-5 h-5 text-gray-500 transition-transform duration-200',
                      isBerthTypeDropdownOpen && 'rotate-180'
                    )}
                  />
                </div>
                {isBerthTypeDropdownOpen && mode !== 'view' && (
                  <div
                    id="berth-type-listbox"
                    className="absolute z-10 w-full mt-2 bg-gray-100 border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                    role="listbox"
                    aria-label="Berth types"
                  >
                    {berthTypeOptions.map((option, idx) => (
                      <div
                        id={`berth-type-option-${option.value}`}
                        key={option.value}
                        role="option"
                        aria-selected={formData.berth_type === option.value}
                        className={clsx(
                          'px-4 py-2 cursor-pointer transition-colors duration-150 text-gray-900 dark:text-gray-900 contrast:text-white',
                          formData.berth_type === option.value &&
                          'bg-blue-100 dark:bg-gray-100 contrast:bg-blue-800',
                          berthTypeHighlightIndex === idx
                            ? 'bg-blue-50 dark:bg-gray-200 contrast:bg-blue-700 ring-1 ring-blue-400 contrast:ring-white'
                            : 'hover:bg-blue-50 dark:hover:bg-gray-300 contrast:hover:bg-blue-700'
                        )}
                        onMouseEnter={() => setBerthTypeHighlightIndex(idx)}
                        onClick={() => handleBerthTypeSelect(option)}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
                {errors.berth_type && (
                  <p
                    id="berth_type-error"
                    className="text-red-500 text-xs mt-2"
                    role="alert"
                  >
                    {errors.berth_type}
                  </p>
                )}
              </div>
            </div>
            {/* Pier */}
            {formData.harbor_id && (
              <div className="col-span-1 pb-1">
                <label
                  id="pier_id-label"
                  htmlFor="pier_id-input"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  Pier
                </label>
                <div className="relative" ref={pierDropdownRef}>
                  <div
                    id="pier_id-input"
                    className={clsx(
                      'input input-bordered w-full h-12 flex items-center justify-between rounded-lg bg-gray-200 border-2 transition-all duration-200',
                      {
                        'cursor-pointer hover:border-blue-500':
                          !isSubmitting && mode !== 'view',
                        'cursor-not-allowed bg-gray-100 opacity-50':
                          isSubmitting || mode === 'view',
                        'border-blue-500 shadow-md':
                          isPierDropdownOpen && mode !== 'view',
                        'border-red-500': errors.pier_id,
                      }
                    )}
                    ref={pierButtonRef}
                    tabIndex={0}
                    onClick={handlePierDropdownToggle}
                    onKeyDown={handlePierKeyDown}
                    aria-labelledby="pier_id-label"
                    aria-invalid={!!errors.pier_id}
                    aria-describedby={
                      errors.pier_id ? 'pier_id-error' : undefined
                    }
                    aria-haspopup="listbox"
                    aria-expanded={isPierDropdownOpen}
                    aria-activedescendant={
                      isPierDropdownOpen &&
                        pierHighlightIndex >= 0 &&
                        filteredPiers[pierHighlightIndex]
                        ? `pier-option-${filteredPiers[pierHighlightIndex].id}`
                        : undefined
                    }
                    aria-controls="pier-listbox"
                    role="combobox"
                  >
                    <div className="truncate text-gray-900">
                      {formData.pier?.name ||
                        piersData?.data?.find(
                          (pier: { id: string; name: string }) =>
                            pier.id === formData.pier_id
                        )?.name ||
                        'Select pier'}
                    </div>
                    <div className="flex items-center">
                      <svg
                        className={clsx(
                          'w-5 h-5 text-gray-400 transition-transform duration-200',
                          isPierDropdownOpen && 'transform rotate-180'
                        )}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                  {isPierDropdownOpen && mode !== 'view' && (
                    <div className="absolute z-10 w-full mt-2 bg-gray-100 border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-hidden flex flex-col">
                      <div className="p-3 border-b">
                        <input
                          ref={pierSearchInputRef}
                          type="text"
                          placeholder="Search piers..."
                          className="w-full p-2 border bg-gray-200 placeholder:text-gray-400 rounded-lg focus:ring-blue-500 focus:outline-none"
                          value={pierSearchTerm}
                          onChange={(e) => {
                            setPierSearchTerm(e.target.value);
                            setIsPierDropdownOpen(true);
                          }}
                          onKeyDown={(e) => {
                            const filtered = getFilteredPiers();
                            if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              setPierHighlightIndex((prev) => {
                                const next =
                                  prev < 0
                                    ? 0
                                    : Math.min(
                                      prev + 1,
                                      Math.max(filtered.length - 1, 0)
                                    );
                                return next;
                              });
                            } else if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              setPierHighlightIndex((prev) => {
                                const next =
                                  prev < 0 ? 0 : Math.max(prev - 1, 0);
                                return next;
                              });
                            } else if (e.key === 'Tab') {
                              // Use Tab / Shift+Tab to move through options instead of leaving the list
                              e.preventDefault();
                              setPierHighlightIndex((prev) => {
                                if (filtered.length === 0) return -1;
                                if (e.shiftKey) {
                                  const next =
                                    prev < 0 ? 0 : Math.max(prev - 1, 0);
                                  return next;
                                }
                                const next =
                                  prev < 0
                                    ? 0
                                    : Math.min(prev + 1, filtered.length - 1);
                                return next;
                              });
                            } else if (e.key === 'Enter') {
                              e.preventDefault();
                              const index =
                                pierHighlightIndex >= 0
                                  ? pierHighlightIndex
                                  : 0;
                              const option = filtered[index];
                              if (option) handlePierSelect(option);
                            } else if (e.key === 'Escape') {
                              // Close only the dropdown, not the modal
                              e.preventDefault();
                              e.stopPropagation();
                              setIsPierDropdownOpen(false);
                              setTimeout(
                                () => pierButtonRef.current?.focus(),
                                0
                              );
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div
                        id="pier-listbox"
                        role="listbox"
                        className="overflow-y-auto"
                      >
                        {piersData?.data && piersData?.data?.length > 0 ? (
                          piersData?.data
                            ?.filter(
                              (pier: {
                                id: string;
                                name: string;
                                pooled_capacity?: number | null;
                                capacity_mode?: string | null;
                              }) =>
                                pier.name
                                  .toLowerCase()
                                  .includes(pierSearchTerm.toLowerCase())
                            )
                            .map(
                              (
                                pier: {
                                  id: string;
                                  name: string;
                                  pooled_capacity?: number | null;
                                  capacity_mode?: string | null;
                                },
                                idx: number
                              ) => (
                                <div
                                  id={`pier-option-${pier.id}`}
                                  key={pier.id}
                                  role="option"
                                  aria-selected={formData.pier_id === pier.id}
                                  className={clsx(
                                    `px-4 py-2 cursor-pointer transition-colors duration-150 text-gray-900 dark:text-gray-900 contrast:text-white`,
                                    formData.pier_id === pier.id &&
                                    ` bg-blue-100 dark:bg-gray-100 contrast:bg-blue-800`,
                                    pierHighlightIndex === idx
                                      ? ` bg-blue-50 dark:bg-gray-200 contrast:bg-blue-700 ring-1 ring-blue-400 contrast:ring-white`
                                      : ` hover:bg-blue-50 dark:hover:bg-gray-300 contrast:hover:bg-blue-700`
                                  )}
                                  onClick={() => handlePierSelect(pier)}
                                >
                                  <div className="flex justify-between items-center">
                                    <span className="font-medium">
                                      {pier.name}
                                    </span>

                                    {pier.pooled_capacity !== null &&
                                      pier.pooled_capacity !== undefined && (
                                        <span className="ml-2 text-xs text-gray-600 dark:text-gray-700 contrast:text-gray-200">
                                          (Cap: {pier.pooled_capacity})
                                        </span>
                                      )}
                                  </div>
                                </div>
                              )
                            )
                        ) : (
                          <div
                            role="option"
                            aria-disabled="true"
                            className="px-4 py-2 text-gray-500"
                          >
                            {pierSearchTerm
                              ? 'No piers found'
                              : 'No piers available'}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {errors.pier_id && (
                    <p
                      id="pier_id-error"
                      className="text-red-500 text-xs mt-2"
                      role="alert"
                    >
                      {errors.pier_id}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Berth Prefix */}
            {mode === 'create' && (
              <div className="col-span-1 pb-1">
                <label
                  htmlFor="prefix-input"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  Berth Prefix
                </label>
                <input
                  id="prefix-input"
                  type="text"
                  name="pier_prefix"
                  value={prefix}
                  autoComplete="off"
                  onChange={handlePrefixChange}
                  onBlur={() => validateField('pier_prefix', prefix)}
                  disabled={isSubmitting}
                  className={clsx(
                    'input input-bordered w-full h-12 rounded-lg bg-gray-200 border-2 transition-all duration-200',
                    errors.pier_prefix && 'border-red-500',
                    'focus:ring-blue-500 focus:outline-none'
                  )}
                  aria-invalid={!!errors.pier_prefix}
                  aria-describedby={
                    errors.pier_prefix ? 'prefix-error' : undefined
                  }
                  placeholder="Enter berth prefix (e.g., Alpha)"
                />
                {errors.pier_prefix && (
                  <p
                    id="prefix-error"
                    className="text-red-500 text-xs mt-2"
                    role="alert"
                  >
                    {errors.pier_prefix}
                  </p>
                )}
              </div>
            )}

            {/* Berth Quantity (Create Mode Only) */}
            {mode === 'create' && (
              <div className="col-span-1">
                <label
                  htmlFor="quantity-input"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  Total Berths
                  {isSelectedPierSingleBerthPool ? (
                    <span className="text-gray-500 text-xs ml-2">
                      (fixed at 1)
                    </span>
                  ) : (
                    selectedPierPooledCapacity !== null && (
                      <span className="text-gray-500 text-xs ml-2">
                        (Max: {selectedPierPooledCapacity})
                      </span>
                    )
                  )}
                </label>
                <NumberInput
                  id="quantity-input"
                  name="quantity"
                  value={
                    isSelectedPierSingleBerthPool
                      ? 1
                      : localFormData.quantity || ''
                  }
                  onChange={handleChange}
                  onBlur={(e) => validateField(e.target.name, e.target.value)}
                  disabled={isSubmitting || isSelectedPierSingleBerthPool}
                  min="1"
                  max={
                    isSelectedPierSingleBerthPool
                      ? 1
                      : (selectedPierPooledCapacity ?? undefined)
                  }
                  className={clsx(
                    'input input-bordered w-full h-12 rounded-lg bg-gray-200 border-2 transition-all duration-200',
                    errors.quantity && 'border-red-500',
                    'focus:ring-blue-500 focus:outline-none'
                  )}
                  aria-invalid={!!errors.quantity}
                  aria-describedby={
                    errors.quantity ? 'quantity-error' : undefined
                  }
                  placeholder="Number of berths to create"
                />
                {isSelectedPierSingleBerthPool ? (
                  <p className="text-gray-500 text-xs mt-2">
                    This pier represents its whole pooled capacity through a
                    single berth - only one can ever be created here.
                  </p>
                ) : (
                  selectedPierPooledCapacity !== null && (
                    <p className="text-gray-500 text-xs mt-2">
                      This pier has a pooled capacity of{' '}
                      {selectedPierPooledCapacity} berths
                    </p>
                  )
                )}
                {errors.quantity && (
                  <p
                    id="quantity-error"
                    className="text-red-500 text-xs mt-2"
                    role="alert"
                  >
                    {errors.quantity}
                  </p>
                )}
              </div>
            )}

            {/* Generated Berth Numbers (Create Mode Only) */}
            {mode === 'create' && (
              <div className="col-span-1">
                <span
                  id="berth_numbers-label"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  Generated Berth Numbers
                </span>
                <div
                  id="berth_numbers-display"
                  className="input input-bordered w-full h-12 rounded-lg bg-gray-100 text-gray-700 flex items-center truncate"
                  aria-labelledby="berth_numbers-label"
                  role="text"
                >
                  {generatedBerthRange || 'No berths generated'}
                </div>
              </div>
            )}

            {/* Category */}
            <div className="col-span-1 pb-1">
              <label
                htmlFor="category_id-input"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Category
              </label>
              <select
                id="category_id-input"
                name="category_id"
                value={localFormData.category_id || ''}
                onChange={handleChange}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                disabled={
                  mode === 'view' || isSubmitting || isCategoriesLoading
                }
                className={clsx(
                  'select select-bordered w-full h-12 rounded-lg bg-gray-200 border-2 transition-all duration-200',
                  errors.category_id && 'border-red-500',
                  'focus:ring-blue-500 focus:outline-none'
                )}
                aria-invalid={!!errors.category_id}
                aria-describedby={
                  errors.category_id ? 'category_id-error' : undefined
                }
              >
                <option value="" disabled>
                  Select a category
                </option>
                {categories?.data?.map((category: IBerthCategoryData) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors.category_id && (
                <p
                  id="category_id-error"
                  className="text-red-500 text-xs mt-2"
                  role="alert"
                >
                  {errors.category_id}
                </p>
              )}
            </div>

            {/* Mooring Style */}
            <div className="col-span-1 pb-1">
              {mode === 'view' ? (
                <>
                  <span
                    id="mooring_style-label"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Mooring Style
                  </span>
                  <div
                    id="mooring_style-input"
                    className="input input-bordered w-full h-12 rounded-lg bg-gray-100 text-gray-900 flex items-center"
                    aria-labelledby="mooring_style-label"
                    role="text"
                  >
                    {moorings?.find(
                      (m: Mooring) => m.name === localFormData.mooring_style
                    )?.label ||
                      localFormData.mooring_style ||
                      'N/A'}
                  </div>
                </>
              ) : (
                <>
                  <label
                    htmlFor="mooring_style-input"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Mooring Style
                  </label>
                  <select
                    id="mooring_style-input"
                    name="mooring_style"
                    value={localFormData.mooring_style || ''}
                    onChange={handleChange}
                    onBlur={(e) => validateField(e.target.name, e.target.value)}
                    disabled={isSubmitting || isMooringsLoading}
                    className={clsx(
                      'select select-bordered w-full h-12 rounded-lg bg-gray-200 border-2 transition-all duration-200',
                      errors.mooring_style && 'border-red-500',
                      'focus:ring-blue-500 focus:outline-none'
                    )}
                    aria-invalid={!!errors.mooring_style}
                    aria-describedby={
                      errors.mooring_style ? 'mooring_style-error' : undefined
                    }
                  >
                    <option value="" disabled>
                      Select a mooring style
                    </option>
                    {moorings?.map((mooring: Mooring) => (
                      <option key={mooring.id} value={mooring.name}>
                        {mooring.label}
                      </option>
                    ))}
                  </select>
                </>
              )}
              {errors.mooring_style && (
                <p
                  id="mooring_style-error"
                  className="text-red-500 text-xs mt-2"
                  role="alert"
                >
                  {errors.mooring_style}
                </p>
              )}
            </div>

            {/* Berth Number (Edit/View Mode) */}
            {(mode === 'edit' || mode === 'view') && localFormData.harbor && (
              <>
                <div className="col-span-1">
                  <label
                    htmlFor="berth_number-input"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Berth Number
                  </label>
                  <input
                    id="berth_number-input"
                    type="text"
                    name="berth_number"
                    value={localFormData.berth_number || ''}
                    onChange={handleChange}
                    onBlur={(e) => validateField(e.target.name, e.target.value)}
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      'input input-bordered w-full h-12 rounded-lg bg-gray-200 border-2 transition-all duration-200',
                      errors.berth_number && 'border-red-500',
                      mode === 'view' && 'bg-gray-100 opacity-50',
                      'focus:ring-blue-500 focus:outline-none'
                    )}
                    aria-invalid={!!errors.berth_number}
                    aria-describedby={
                      errors.berth_number ? 'berth_number-error' : undefined
                    }
                    placeholder="Enter berth number"
                    autoComplete="off"
                  />
                  {errors.berth_number && (
                    <p
                      id="berth_number-error"
                      className="text-red-500 text-xs mt-2"
                      role="alert"
                    >
                      {errors.berth_number}
                    </p>
                  )}
                </div>

                <div className="col-span-1">
                  <div className="mb-4">
                    <span
                      id="harbor-location-label"
                      className="block text-sm font-medium text-gray-900 mb-2"
                    >
                      Harbor Location
                    </span>
                    <div
                      id="harbor-location"
                      className="input input-bordered w-full h-12 rounded-lg bg-gray-100 text-gray-900"
                      aria-labelledby="harbor-location-label"
                      role="text"
                    >
                      {localFormData.harbor?.location || 'N/A'}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Amenities */}
            <div className="col-span-1 md:col-span-2">
              <label
                id="amenities-label"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Amenities
              </label>
              <div
                role="group"
                aria-labelledby="amenities-label"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
              >
                {amenityOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-200 transition-colors duration-150"
                  >
                    <input
                      type="checkbox"
                      value={option.value}
                      checked={
                        localFormData.amenities?.includes(option.value) ?? false
                      }
                      onChange={handleAmenityChange}
                      disabled={mode === 'view' || isSubmitting}
                      className={clsx(
                        'checkbox checkbox-primary h-5 w-5 cursor-pointer',
                        errors.amenities && 'border-red-500'
                      )}
                      aria-invalid={!!errors.amenities}
                      aria-describedby={
                        errors.amenities ? 'amenities-error' : undefined
                      }
                    />
                    <span className="text-sm text-gray-900 cursor-pointer">
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
              {errors.amenities && (
                <p
                  id="amenities-error"
                  className="text-red-500 text-xs mt-2"
                  role="alert"
                >
                  {errors.amenities}
                </p>
              )}
            </div>

            {/* Price */}
            {!shouldShowSeasonalPricing && (
              <div className="col-span-1 pb-1">
                <label
                  htmlFor="price-input"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  Price
                  {(mode === 'create' || mode === 'edit') &&
                    (Number(localFormData.berth_type) === BerthType.TRANSIENT ||
                      Number(localFormData.berth_type) === BerthType.YEARLY) &&
                    ' *'}
                </label>
                <PriceInput
                  id="price-input"
                  name="price"
                  value={localFormData.price ?? ''}
                  onChange={(val) => {
                    setLocalFormData((prev) => ({
                      ...prev,
                      price: val,
                    }));
                    setFormData((prev) => ({
                      ...prev,
                      price: val,
                    }));
                    validateField('price', val);
                  }}
                  disabled={mode === 'view' || isSubmitting}
                  inputClassName={clsx(
                    'input input-bordered w-full h-12 rounded-lg bg-gray-200 border-2 transition-all duration-200',
                    errors.price && 'border-red-500',
                    mode === 'view' && 'bg-gray-100 opacity-50',
                    'focus:ring-blue-500 focus:outline-none'
                  )}
                  error={errors.price}
                  placeholder="Enter berth price"
                />
              </div>
            )}

            {shouldShowSeasonalPricing && (
              <div className="col-span-1 md:col-span-2">
                <div className="text-sm font-semibold text-gray-900 contrast:text-white">
                  Seasonal Pricing (Inherited from Harbor)
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Season dates are defined at harbor level and cannot be edited
                  here.
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                  {[
                    {
                      key: Seasons.SPRING,
                      label: 'Spring',
                      badgeBg: 'green',
                      data: selectedHarborForSeasonal?.seasonal_dates?.spring,
                    },
                    {
                      key: Seasons.SUMMER,
                      label: 'Summer',
                      badgeBg: 'sky',
                      data: selectedHarborForSeasonal?.seasonal_dates?.summer,
                    },
                    {
                      key: Seasons.AUTUMN,
                      label: 'Autumn',
                      badgeBg: 'orange',
                      data: selectedHarborForSeasonal?.seasonal_dates?.autumn,
                    },
                    {
                      key: Seasons.WINTER,
                      label: 'Winter',
                      badgeBg: 'indigo',
                      data: selectedHarborForSeasonal?.seasonal_dates?.winter,
                    },
                  ].map(({ key, label, badgeBg, data }) => (
                    <div
                      key={key}
                      className="rounded-xl border border-gray-200 dark:border-gray-700 contrast:border-gray-800 bg-gray-200 p-4 shadow-sm min-h-[320px] flex flex-col"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {label}
                          </div>
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            Harbor season dates
                          </div>
                        </div>
                        <div
                          className={clsx(
                            'rounded-full text-[11px] px-2 py-1 border',
                            `bg-${badgeBg}-50 text-${badgeBg}-700 border-${badgeBg}-100`,
                            `dark:bg-${badgeBg}-900 dark:text-${badgeBg}-200 dark:border-${badgeBg}-800`,
                            `contrast:bg-${badgeBg}-900 contrast:text-${badgeBg}-200 contrast:border-${badgeBg}-800`
                          )}
                        >
                          Pricing
                        </div>
                      </div>

                      <div className="mt-3 rounded-lg bg-gray-200 border border-gray-200 p-3 flex-1 overflow-hidden">
                        <div className="text-[11px] font-medium text-gray-700 mb-2">
                          Date ranges
                        </div>

                        <div className="space-y-2 text-xs text-gray-800 overflow-auto max-h-40 pr-1">
                          {data && data.length > 0 ? (
                            data.map((r, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-3"
                              >
                                <span className="font-medium">
                                  {r.start_date || '-'}
                                </span>
                                <span className="text-gray-400">→</span>
                                <span className="font-medium">
                                  {r.end_date || '-'}
                                </span>
                                {r.is_closed ? (
                                  <span className="ml-auto rounded-full bg-red-700 dark:bg-red-800 contrast:bg-red-900 text-white text-[11px] px-2 py-0.5 border border-red-700 dark:border-red-800 contrast:border-red-800">
                                    Closed
                                  </span>
                                ) : (
                                  <span className="ml-auto rounded-full bg-gray-700 text-gray-100 text-[11px] px-2 py-0.5 border border-gray-700 dark:border-gray-600 contrast:border-gray-800">
                                    Open
                                  </span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="text-gray-500 dark:text-gray-400 contrast:text-gray-300">
                              No ranges defined
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 contrast:border-gray-800">
                        <label
                          htmlFor={`season-price-${key}`}
                          className="block text-xs font-semibold text-gray-900 mb-1"
                        >
                          Price ({label})
                        </label>
                        <PriceInput
                          id={`season-price-${key}`}
                          name={key}
                          value={String(seasonalPricing[key] ?? '')}
                          onChange={(val) => {
                            setSeasonalPricing((prev) => ({
                              ...prev,
                              [key]: Number(val) || 0,
                            }));
                          }}
                          disabled={mode === 'view' || isSubmitting}
                          inputClassName={clsx(
                            'input input-bordered w-full h-12 rounded-lg bg-gray-100 border-gray-300 text-gray-900 transition-all',
                            mode === 'view' && 'opacity-50',
                            'focus:ring-blue-500 focus:outline-none'
                          )}
                          placeholder={
                            seasonalClosedMap[key]
                              ? `Enter ${label.toLowerCase()} price for closed season`
                              : `Enter ${label.toLowerCase()} price`
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status */}
            <div className="col-span-1 pb-1">
              <label
                htmlFor="status-input"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Status
              </label>
              <select
                id="status-input"
                name="status"
                value={
                  localFormData.status !== undefined ? localFormData.status : ''
                }
                onChange={handleChange}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'select select-bordered w-full h-12 rounded-lg bg-gray-200 border-2 transition-all duration-200',
                  errors.status && 'border-red-500',
                  mode === 'view' && 'bg-gray-100 opacity-50',
                  'focus:ring-blue-500 focus:outline-none'
                )}
                aria-invalid={!!errors.status}
                aria-describedby={errors.status ? 'status-error' : undefined}
              >
                <option value="" disabled>
                  Select status
                </option>
                <option value={BerthStatus.AVAILABLE}>Available</option>
                {mode === 'edit' && (
                  <option value={BerthStatus.NOT_AVAILABLE}>
                    Not Available
                  </option>
                )}
              </select>
              {errors.status && (
                <p
                  id="status-error"
                  className="text-red-500 text-xs mt-2"
                  role="alert"
                >
                  {errors.status}
                </p>
              )}
            </div>

            {/* Start Date and End Date (shown when status is Not Available) */}
            {Number(localFormData.status) == BerthStatus.NOT_AVAILABLE && (
              <>
                <div className="col-span-1">
                  <label
                    htmlFor="start_date-input"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Start Date
                  </label>
                  <input
                    id="start_date-input"
                    type="date"
                    name="start_date"
                    min={new Date().toISOString().split('T')[0]}
                    value={localFormData.start_date || ''}
                    onChange={handleUnavailabilityChange}
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      'input input-bordered w-full h-12 rounded-lg bg-gray-200 border-2 transition-all duration-200',
                      errors.start_date && 'border-red-500',
                      mode === 'view' && 'bg-gray-100 opacity-50',
                      'focus:ring-blue-500 focus:outline-none'
                    )}
                    aria-invalid={!!errors.start_date}
                    aria-describedby={
                      errors.start_date ? 'start_date-error' : undefined
                    }
                    onKeyDown={(e) => {
                      if (e.key !== 'Tab') {
                        e.preventDefault();
                      }
                    }}
                  />
                  {errors.start_date && (
                    <p
                      id="start_date-error"
                      className="text-red-500 text-xs mt-2"
                      role="alert"
                    >
                      {errors.start_date}
                    </p>
                  )}
                </div>
                <div className="col-span-1">
                  <label
                    htmlFor="end_date-input"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    End Date
                  </label>
                  <input
                    id="end_date-input"
                    type="date"
                    name="end_date"
                    min={localFormData.start_date || ''}
                    value={localFormData.end_date || ''}
                    onChange={handleUnavailabilityChange}
                    disabled={mode === 'view' || isSubmitting}
                    className={clsx(
                      'input input-bordered w-full h-12 rounded-lg bg-gray-200 border-2 transition-all duration-200',
                      errors.end_date && 'border-red-500',
                      mode === 'view' && 'bg-gray-100 opacity-50',
                      'focus:ring-blue-500 focus:outline-none'
                    )}
                    aria-invalid={!!errors.end_date}
                    aria-describedby={
                      errors.end_date ? 'end_date-error' : undefined
                    }
                    onKeyDown={(e) => {
                      if (e.key !== 'Tab') {
                        e.preventDefault();
                      }
                    }}
                  />
                  {errors.end_date && (
                    <p
                      id="end_date-error"
                      className="text-red-500 text-xs mt-2"
                      role="alert"
                    >
                      {errors.end_date}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="mt-8 flex justify-end gap-4">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-sm btn-ghost text-gray-600 bg-gray-300 rounded-lg px-5 py-5 focus:outline-none focus:ring-gray-500 transition-all duration-200 contrast:text-white"
            disabled={isSubmitting}
            aria-label={mode === 'view' ? 'Close' : 'Cancel'}
          >
            {mode === 'view' ? 'Close' : 'Cancel'}
          </button>
          {(mode === 'create' || mode === 'edit') && (
            <button
              type="submit"
              data-testid="modal-submit"
              onClick={onSubmit}
              className="btn btn-sm btn-primary text-white rounded-lg px-5 py-5 focus:outline-none focus:ring-blue-500 bg-blue-600 hover:from-blue-700 hover:to-teal-600 transition-all duration-200"
              aria-label={mode === 'create' ? 'Create Berth' : 'Save Changes'}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Saving...'
                : mode === 'create'
                  ? 'Create Berths'
                  : 'Save'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export { BerthModal };
